import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Stack } from '@mui/material';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { ActionEntry, DashboardGroupHeading } from '@retail/dashboard/components';
import { useDashboardTFunction } from '@retail/dashboard/i18n';
import { mgPrisRoutes } from '@retail/mgpris/config';
import { useFetchPriceControlAggregated } from '@retail/price-control/data-access';
import { useFetchDraftConditions } from '@retail/calculus/data-access';
import { CalculusArea, ConditionsNewDTO } from '@retail/calculus/types';

const numberFormat = new Intl.NumberFormat('nb-NO');

const validFromByArea = (condition: ConditionsNewDTO, area: CalculusArea): string | undefined => {
  switch (area) {
    case 'PURCHASE':
      return condition.purchaseCondition?.validFrom;
    case 'FREIGHT':
      return condition.freightCondition?.validFrom;
    case 'MARKUP':
      return condition.markupCondition?.validFrom;
    case 'CUSTOMER_MARKUP':
      return condition.customerMarkupCondition?.validFrom;
    case 'ROUNDING':
      return condition.roundingCondition?.validFrom;
    case 'PRICE_SOURCE':
      return condition.priceSourceCondition?.validFrom;
    default:
      return undefined;
  }
};

const earliestValidFrom = (condition: ConditionsNewDTO): string | undefined => {
  const dates = condition.types
    .map(({ type }) => validFromByArea(condition, type))
    .filter((d): d is string => Boolean(d));
  if (dates.length === 0) return undefined;
  return dates.reduce((min, d) => (dayjs(d).isBefore(min) ? d : min));
};

export const DashboardActions = () => {
  const t = useDashboardTFunction();
  const { assortmentId } = useSelectedOrgUnit();

  const priceControl = useFetchPriceControlAggregated(assortmentId, {
    refetchOnWindowFocus: false,
    suspense: false,
  });
  const draftConditions = useFetchDraftConditions({
    assortmentId,
    refetchOnWindowFocus: false,
    suspense: false,
  });

  const conditions = useMemo(() => {
    const today = dayjs().startOf('day');
    const list = draftConditions.data ?? [];
    const past = list.filter((d) => {
      const validFrom = earliestValidFrom(d.condition);
      return validFrom ? dayjs(validFrom).isBefore(today) : false;
    }).length;
    return { total: list.length, past, future: list.length - past };
  }, [draftConditions.data]);

  const suppliersInQueue = priceControl.data?.suppliersInQueueAmount ?? 0;
  const itemsToControl = priceControl.data?.totalItemsInControl ?? 0;

  const showPriceControl =
    priceControl.isLoading || priceControl.isError || suppliersInQueue > 0 || itemsToControl > 0;
  const showConditions =
    draftConditions.isLoading || draftConditions.isError || conditions.total > 0;

  if (!showPriceControl && !showConditions) {
    return null;
  }

  return (
    <Stack gap={1.5}>
      <DashboardGroupHeading
        title={t('dashboard.actions.title')}
        icon={<ReportProblemOutlinedIcon fontSize="small" />}
      />

      {showPriceControl && (
        <ActionEntry
          value={numberFormat.format(suppliersInQueue)}
          label={t('dashboard.actions.priceControl.label')}
          detail={t('dashboard.actions.priceControl.detail', {
            items: numberFormat.format(itemsToControl),
            suppliers: numberFormat.format(suppliersInQueue),
          })}
          to={mgPrisRoutes.priceControl.getFullLinkPath()}
          tone={suppliersInQueue > 0 ? 'error' : 'default'}
          isLoading={priceControl.isLoading}
          isError={priceControl.isError}
        />
      )}

      {showConditions && (
        <ActionEntry
          value={numberFormat.format(conditions.total)}
          label={t('dashboard.actions.conditions.label')}
          detail={
            conditions.past > 0
              ? t('dashboard.actions.conditions.detailPast', { count: conditions.past })
              : t('dashboard.actions.conditions.detailFuture', { count: conditions.future })
          }
          to={mgPrisRoutes.approval.getFullLinkPath()}
          tone={conditions.past > 0 ? 'error' : 'default'}
          isLoading={draftConditions.isLoading}
          isError={draftConditions.isError}
        />
      )}
    </Stack>
  );
};
